import { BaseEditorState } from "./common.types"

type NbaEditorForChannel<C extends NBARequest> = C extends "info"
  ? NbaInfoEditorState
  : C extends "games"
  ? NbaGamesEditorState
  : C extends "players"
  ? NbaPlayersEditorState
  : C extends "stats"
  ? NbaStatsEditorState
  : C extends "team"
  ? NbaTeamEditorState
  : never

export type NbaEditorState =
  | NbaInfoEditorState
  | NbaGamesEditorState
  | NbaPlayersEditorState
  | NbaStatsEditorState
  | NbaTeamEditorState

const allNBARequestNames = [
  "info",
  "games",
  "players",
  "team",
  "stats"
] as const

export type NBARequest = typeof allNBARequestNames[number] | never

export const NBARequest = {
  all: allNBARequestNames
}

const allNbaYears = [2016, 2017, 2018] as const
/** Supported years for the NBA API */
export type NbaYear = typeof allNbaYears[number] | never
export const NbaYear = {
  /** List of all years for NBA API */
  all: allNbaYears
}

const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const
type Month = typeof months[number] | never
export const Month = {
  all: months
}

const days = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  19,
  20,
  21,
  22,
  23,
  24,
  25,
  26,
  27,
  28,
  29,
  30,
  31
] as const

export type Day = typeof days[number] | never
export const Day = {
  all: days
}

const teams = [
  "ATL",
  "BKN",
  "BOS",
  "CHA",
  "CHI",
  "CLE",
  "DAL",
  "DEN",
  "DET",
  "GSW",
  "HOU",
  "IND",
  "LAC",
  "LAL",
  "MEM",
  "MIA",
  "MIL",
  "MIN",
  "NOP",
  "NYK",
  "OKC",
  "ORL",
  "PHI",
  "PHX",
  "POR",
  "SAC",
  "SAS",
  "TOR",
  "UTA",
  "WAS"
] as const

export type NbaTeam = typeof teams[number] | never
export const NbaTeam = {
  all: teams,
  isNbaTeam: (team: unknown): team is NbaTeam => teams.includes(team as NbaTeam)
}

export const DefaultNbaRequestsForChannel: {
  [channel in NBARequest]: NbaEditorForChannel<channel>
} = {
  info: { api: "NBA", channel: "info" },
  games: {
    api: "NBA",
    channel: "games",
    year: 2018,
    month: 1,
    day: 24,
    teamId: "HOU"
  },
  players: {
    api: "NBA",
    channel: "players",
    lastName: "Curry",
    firstName: "Stephen"
  },
  stats: {
    api: "NBA",
    channel: "stats",
    lastName: "Harden",
    firstName: "James",
    year: 2018,
    month: 1,
    day: 23
  },
  team: {
    api: "NBA",
    channel: "team",
    retrieve: "schedule",
    teamId: "LAL",
    season: "2017-2018"
  }
}

interface NbaGamesEditorState extends BaseNbaEditorState<"games"> {
  year: NbaYear
  month: Month
  day: Day
  teamId: NbaTeam
}

interface NbaPlayersEditorState extends BaseNbaEditorState<"players"> {
  lastName: string
  firstName: string
}

interface NbaInfoEditorState extends BaseNbaEditorState<"info"> {}

interface NbaStatsEditorState extends BaseNbaEditorState<"stats"> {
  lastName: string
  firstName: string
  year: NbaYear
  month: Month
  day: Day
}

const nbaSeasons = ["2016-2017", "2017-2018", "2018-2019"] as const

export type NbaSeason = typeof nbaSeasons[number] | never
export const NbaSeason = {
  all: nbaSeasons
}

const allRetrieves = ["roster", "schedule"] as const
/** Valid values for the `retrieve` property */
export type Retrieve = typeof allRetrieves[number] | never

export const Retrieve = {
  all: allRetrieves
}

interface NbaTeamEditorState extends BaseNbaEditorState<"team"> {
  retrieve: Retrieve
  teamId: NbaTeam
  season: NbaSeason
}

interface BaseNbaEditorState<C extends NBARequest>
  extends BaseEditorState<"NBA"> {
  channel: C
}
